import { useShowNotification } from '@inflearn/ds-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useTranslation } from 'next-i18next';
import { API_ENDPOINT } from '../../resources/@types/apiEndpoint';
import {
  useClientApiV1CartCreateMutation,
  useClientApiV1CartDeleteMutation,
} from '../../resources/@types/courseSchema';
import { ResponseStatus } from '../../resources/@types/uccSchema';
import { ApiError } from '../../resources/ApiError';
import { eventNames } from '../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../utils/GTM/useTrackMixpanelEvent';
import { type UserProfileCourseDto, adaptToMixpanelCourse } from '../utils/course/courseAdaptor';

// TODO 추후 useCart 파일 하나로 통합 예정
type Props = {
  initialValue: boolean;
  course: UserProfileCourseDto;
};

/** AppShell 헤더의 장바구니 invalidate를 위한 KEY */
const CART_KEY = '/v1/cart';

export const useCart = ({ initialValue, course }: Props) => {
  const [isInCart, setIsInCart] = useState(initialValue);

  useEffect(() => {
    setIsInCart(initialValue);
  }, [initialValue]);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const { showNotification } = useShowNotification();

  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const { mutate: addToCart } = useClientApiV1CartCreateMutation({
    url: API_ENDPOINT.CLIENT_V1_CART,
    options: {
      onMutate: () => {
        setIsInCart(true);
      },
      onSuccess: ({ data }) => {
        showNotification({
          type: 'success',
          title: t('수강바구니에 추가되었습니다.'),
        });

        void queryClient.invalidateQueries([CART_KEY]);

        trackMixpanelEvent(eventNames.ADD_TO_CART, {
          cart_id: data.items[0] ? String(data.items[0].cartId) : undefined,
          spot: 'user-profile',
          ...adaptToMixpanelCourse(course),
        });
      },
      onError: (error) => {
        if (error instanceof ApiError && error.statusCode === ResponseStatus.BAD_REQUEST) {
          showNotification({
            type: 'error',
            title: t('공개되지 않은 강의로 수강이 제한됩니다.'),
          });
        } else {
          showNotification({
            type: 'error',
            title: t('수강바구니 추가에 실패했습니다.'),
            message: t('잠시 후 다시 시도해주세요.'),
          });
        }

        setIsInCart(false);
      },
    },
  });

  const { mutate: removeFromCart } = useClientApiV1CartDeleteMutation({
    url: API_ENDPOINT.CLIENT_V1_CART,
    options: {
      onMutate: () => {
        setIsInCart(false);
      },
      onSuccess: () => {
        showNotification({
          type: 'success',
          title: t('수강바구니에서 제거되었습니다.'),
        });

        void queryClient.invalidateQueries([CART_KEY]);
      },
      onError: (error) => {
        if (error instanceof ApiError && error.statusCode === ResponseStatus.BAD_REQUEST) {
          showNotification({
            type: 'error',
            title: t('공개되지 않은 강의로 수강이 제한됩니다.'),
          });
        } else {
          showNotification({
            type: 'error',
            title: t('수강바구니 삭제에 실패했습니다.'),
            message: t('잠시 후 다시 시도해주세요.'),
          });
        }

        setIsInCart(true);
      },
    },
  });

  return { isInCart, addToCart, removeFromCart };
};
